import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import type { ChatAppsNames } from "@whatslly/types/ChatAppsNames";
import type { IStorage } from "@whatslly/commons/storage.constants";
import { InjectStorage, STORAGE } from "@whatslly/commons/storage.constants";
import { webScriptClient } from "@whatslly/instant-messaging/webScriptClient";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import { backgroundClient } from "@whatslly/commons/background/client";
import { BgCommands } from "@whatslly/commons/bgCommands";
import { GetInformationalMessageResponse } from "@whatslly/types/integrations/api/req/GetInformationalMessageRequest";
import { fromPromise } from "mobx-utils";
export const MAX_FAVORITES = 10;

@injectable()
export class AppStore {
  /**
   * @deprecated - use the scss `themify` mixin instead
   */
  darkMode?: boolean;
  isInitiated = false;
  isActionsOpen = false;
  wapVersion = "";
  chatApp: ChatAppsNames = "whatsapp";
  widgetNotificationHeight = 0;
  isNotificationExists = false;
  isCalendarOpen = false;
  isValidBrowserVersion = true;
  lastSeenInformationalMessageId: string | null = null;
  favoriteActions: { actionName: string; isNew: boolean; isRemoved: boolean }[] = [];
  actionsMenuFavorites: string[] = [];
  isAppOpen = false;
  forceOpenApp = false;
  isRemoveFavoriteActionOpen = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateAvailable: { platform: "desktop" | "browserExt" | ""; message: any } = {
    platform: "",
    message: "",
  };
  isDesktop?: boolean;
  blurModeIsActive = false;
  isLoading = false;
  constructor(@inject(InjectStorage) private storage: IStorage) {
    makeObservable(this, {
      darkMode: observable,
      isActionsOpen: observable,
      wapVersion: observable,
      isRemoveFavoriteActionOpen: observable,
      isNotificationExists: observable,
      widgetNotificationHeight: observable,
      isCalendarOpen: observable,
      isValidBrowserVersion: observable,
      lastSeenInformationalMessageId: observable,
      isInitiated: observable,
      seenInformationalMessageId: computed,
      informationalMessage: computed,
      updateAvailable: observable,
      favoriteActions: observable,
      actionsMenuFavorites: observable,
      isAppOpen: observable,
      forceOpenApp: observable,
      isDesktop: observable,
      blurModeIsActive: observable,
      isLoading: observable,
      openActions: action,
      toggleActions: action,
      closeActions: action,
      changeNotificationStatus: action,
      updateWidgetNotificationHeight: action,
      setCalendarOpen: action,
      setDarkMode: action,
      initFavoriteActions: action,
      setFavoriteAction: action,
      tempRemoveForAnimation: action,
      removeFavoriteAction: action,
      setForceAppOpen: action,
      toggleIsAppOpen: action,
      setIsDesktop: action,
      setWapVersion: action,
      setBlurModeIsActive: action,
      setIsLoading: action,
    });
  }

  setWapVersion(wapVersion: string) {
    this.wapVersion = wapVersion;
  }

  closeActions() {
    if (this.isActionsOpen) {
      this.isActionsOpen = false;
      this.isCalendarOpen = false;
    }
  }

  openActions() {
    if (!this.isActionsOpen) {
      this.isActionsOpen = true;
    }
  }

  toggleActions() {
    this.isActionsOpen = !this.isActionsOpen;
  }

  setCalendarOpen(val: boolean) {
    this.isCalendarOpen = val;
  }

  updateWidgetNotificationHeight(height: number) {
    this.widgetNotificationHeight = height;
  }

  changeNotificationStatus(status: boolean) {
    this.isNotificationExists = status;
  }

  setDarkMode(val: boolean) {
    this.darkMode = val;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setUpdateAvailable({ platform, message }: { platform: "desktop" | "browserExt" | ""; message: any }) {
    this.updateAvailable = { platform, message };
  }

  async setIsValidBrowserVersion() {
    const isValidBrowserVersion = await this.storage.get(STORAGE.IS_VALID_BROWSER_VERSION);
    runInAction(() => {
      this.isValidBrowserVersion = (() => {
        if (typeof isValidBrowserVersion === "undefined") {
          return true;
        }
        return Boolean(isValidBrowserVersion);
      })();
    });
  }

  async getInformationalMessage() {
    const res = await backgroundClient.send<typeof BgCommands.HTTP_REQUEST, GetInformationalMessageResponse>(BgCommands.HTTP_REQUEST, {
      method: "GET",
      urlSuffix: "/integrations/api/informational-message",
    });

    return res.success ? res.data : null;
  }

  async setViewedInformationalMessage(id: string) {
    await this.storage.set(STORAGE.SEEN_INFORMATIONAL_MESSAGE_ID, id);
    runInAction(() => {
      this.lastSeenInformationalMessageId = id;
    });
  }

  get seenInformationalMessageId() {
    if (!this.lastSeenInformationalMessageId) {
      return fromPromise(
        new Promise<string | null>((resolve) => {
          this.storage.get(STORAGE.SEEN_INFORMATIONAL_MESSAGE_ID).then((id) => {
            runInAction(() => {
              this.lastSeenInformationalMessageId = id;
            });
            resolve(id);
          });
        })
      );
    }

    return fromPromise(Promise.resolve(this.lastSeenInformationalMessageId));
  }

  get informationalMessage() {
    return fromPromise(this.getInformationalMessage());
  }

  setBlurModeIsActive(newState: boolean) {
    this.blurModeIsActive = newState;
  }

  /**
   *  We are calling this function at the Widgetapp file use effect to initiate the favorite actions
   */
  async initFavoriteActions(orgDefaultFavoriteActions: string[]) {
    const favoritesActions = await this.storage.get(STORAGE.FAVORITE_ACTIONS);
    if (!Array.isArray(favoritesActions)) {
      await this.storage.set(STORAGE.FAVORITE_ACTIONS, orgDefaultFavoriteActions);
      runInAction(() => {
        this.favoriteActions = orgDefaultFavoriteActions.map((actionName) => {
          return {
            isNew: false,
            isRemoved: false,
            actionName,
          };
        });
        this.actionsMenuFavorites = orgDefaultFavoriteActions;
      });
      return orgDefaultFavoriteActions;
    }

    runInAction(() => {
      this.favoriteActions = favoritesActions.map((actionName) => {
        return {
          isNew: false,
          isRemoved: false,
          actionName,
        };
      });
      this.actionsMenuFavorites = favoritesActions;
      this.isInitiated = true;
    });
  }

  /**
   * Save a list of the favorite action in the main store
   * web - chrome.storage
   * electron - ???
   */
  async setFavoriteAction(actionName: string) {
    if (this.actionsMenuFavorites.length >= MAX_FAVORITES) {
      return;
    }
    const oldFavoriteActions = this.actionsMenuFavorites;
    await this.storage.set(STORAGE.FAVORITE_ACTIONS, [...oldFavoriteActions, actionName]);
    runInAction(() => {
      this.actionsMenuFavorites = [...oldFavoriteActions, actionName];
      const favoriteActionIndex = this.favoriteActions.findIndex((oldActionName) => oldActionName.actionName === actionName);
      if (favoriteActionIndex !== -1) {
        this.favoriteActions[favoriteActionIndex] = { actionName, isNew: true, isRemoved: false };
      } else {
        this.favoriteActions.push({ actionName, isNew: true, isRemoved: false });
      }
    });
  }

  /**
   * First we remove the action from the actionsMenuFavorites array
   to tell the side bar and the actions menu to remove the favorite
   but we dont delete it from favoriteActions for letting the animation finish
   after the animation fish it call the {@link AppStore.removeFavoriteAction} function
   */
  async tempRemoveForAnimation(actionNameToRemove: string) {
    const newFavoriteActions = this.actionsMenuFavorites.filter((actionName) => actionName !== actionNameToRemove);
    await this.storage.set(STORAGE.FAVORITE_ACTIONS, newFavoriteActions);

    runInAction(() => {
      this.actionsMenuFavorites = newFavoriteActions;
      const favoriteActionIndex = this.favoriteActions.findIndex((oldActionName) => oldActionName.actionName === actionNameToRemove);
      if (favoriteActionIndex !== -1) {
        this.favoriteActions[favoriteActionIndex] = { actionName: actionNameToRemove, isNew: false, isRemoved: true };
      }
    });
    return;
  }

  /**
   * This function triggered by the side bar favotire removed action
      after the animation finished. PLEASE DO NOT USE THIS FUNCTION TO
      REMOVE FAVORITE ACTION BY HAND - TO REMOVE A FAVORITE ACTION PLEASE
      USE - {@link AppStore.removeFavoriteAction}
   */
  removeFavoriteAction(deletedActionName: string) {
    this.favoriteActions = this.favoriteActions.filter((action) => {
      return action.actionName !== deletedActionName || !action.isRemoved;
    });
  }

  /**
   * Set force open app for cases we want to open the app even
   * if the app is collapsed for the user for example - the user
   * clicked on favorite action that need the closed pannel to work
   */
  setForceAppOpen(forceAppOpen: boolean) {
    this.forceOpenApp = forceAppOpen;
    this.toggleIsAppOpen("open", true);
  }

  /**
   * Toggle the app state & reset force open app, with a option
   * to force app state insted of toggling for cases like open
   * the actions menu while the app is collapsed
   */
  toggleIsAppOpen(forceState?: "open" | "close", isForcedByAppStore?: boolean) {
    this.forceOpenApp = !isForcedByAppStore ? false : true;
    const newStatus = forceState ? (forceState === "open" ? true : false) : !this.isAppOpen;
    this.isAppOpen = newStatus;

    backgroundClient.send(BgCommands.CHANGE_APP_STATUS, { newStatus });

    if (!newStatus) {
      !isForcedByAppStore && backgroundClient.send(BgCommands.TRK, { action: "close" });
      webScriptClient.send(WebScriptCommands.CLOSE_WIDGET);
      return;
    }

    !isForcedByAppStore && backgroundClient.send(BgCommands.TRK, { action: "open" });
    webScriptClient.send(WebScriptCommands.OPEN_WIDGET);
  }

  setIsDesktop() {
    this.isDesktop = true;
  }

  setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }
}
